import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, User, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubscribePage = () => {
  const navigate = useNavigate();
  const { user, checkAuth, signup } = useAuth();
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });

  useEffect(() => {
    // If payment was successful, poll for subscription activation
    if (success === 'true') {
      let attempts = 0;
      const maxAttempts = 10; // Try for up to 10 seconds
      
      const checkSubscriptionStatus = async () => {
        try {
          await checkAuth();
          
          // Check if subscription is now active
          const token = localStorage.getItem('session_token');
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.subscription_status === 'active') {
            // Success! Redirect to app
            navigate('/', { replace: true });
          } else {
            attempts++;
            if (attempts < maxAttempts) {
              // Try again in 1 second
              setTimeout(checkSubscriptionStatus, 1000);
            } else {
              // After 10 attempts, just redirect and let them try again
              alert('Payment processing is taking longer than expected. Please refresh the page.');
              navigate('/', { replace: true });
            }
          }
        } catch (error) {
          console.error('Error checking subscription:', error);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(checkSubscriptionStatus, 1000);
          }
        }
      };
      
      // Start checking after 2 seconds (give webhook time to process)
      setTimeout(checkSubscriptionStatus, 2000);
    }
  }, [success, checkAuth, navigate]);

  // If user is already active, redirect to home
  useEffect(() => {
    if (user && user.subscription_status === 'active') {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      setFormData((prev) => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [user]);

  const handleChange = (event) => {
    setFormData((prev) => ({
      ...prev,
      [event.target.name]: event.target.value
    }));
    setError('');
  };

  const startCheckout = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      throw new Error('Missing session token');
    }

    const response = await axios.post(
      `${API}/stripe/create-checkout-session`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    window.location.href = response.data.checkout_url;
  };

  const handleStartSubscription = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!user) {
        const result = await signup(formData.email, formData.password, formData.name);
        if (!result.success) {
          setError(result.error);
          setLoading(false);
          return;
        }
      }

      await startCheckout();
    } catch (err) {
      console.error('Error starting checkout:', err);
      setError('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  const isAuthenticated = Boolean(user);
  const nameValue = isAuthenticated ? (user?.name || '') : formData.name;
  const emailValue = isAuthenticated ? (user?.email || '') : formData.email;

  // Show success message after payment
  if (success === 'true') {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '16px',
          padding: '3rem',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '500px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: '#d4edda',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <Check size={48} style={{ color: '#155724' }} />
          </div>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem', color: '#333' }}>
            Payment Successful!
          </h1>
          <p style={{ fontSize: '1.2rem', color: '#666', marginBottom: '1rem' }}>
            Activating your subscription...
          </p>
          <p style={{ fontSize: '1rem', color: '#999', marginBottom: '2rem' }}>
            This may take a few seconds
          </p>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #4A90E2',
            borderTop: '4px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2.5rem'
    }}>
      <div style={{ width: '100%', maxWidth: '560px' }}>
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: '16px',
          padding: '2.75rem',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
        }}>
          <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
            <div>
              <h1 style={{ fontSize: '1.9rem', color: '#1f2937', marginBottom: '0.4rem' }}>Start your subscription</h1>
              <p style={{ color: '#64748b', fontSize: '1rem' }}>
                Everything you need to create and manage quotes.
              </p>
            </div>
          </div>

          {isAuthenticated && (
            <div style={{
              marginBottom: '1.25rem',
              padding: '0.75rem 1rem',
              borderRadius: '10px',
              backgroundColor: '#f8fafc',
              color: '#1d4ed8',
              fontSize: '0.95rem'
            }}>
              You&apos;re signed in as {user?.email}. Continue to start your subscription.
            </div>
          )}

          {error && (
            <div style={{
              padding: '1rem',
              backgroundColor: '#fee',
              color: '#c33',
              borderRadius: '8px',
              marginBottom: '1.5rem',
              fontSize: '1rem'
            }}>
              {error}
            </div>
          )}

          <form onSubmit={handleStartSubscription}>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'block',
                fontSize: '1.05rem',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '0.5rem'
              }}>
                <User size={18} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                Full Name
              </label>
              <input
                type="text"
                name="name"
                value={nameValue}
                onChange={handleChange}
                required={!isAuthenticated}
                disabled={isAuthenticated}
                style={{
                  width: '100%',
                  padding: '0.95rem',
                  fontSize: '1.05rem',
                  border: '2px solid #dbeafe',
                  borderRadius: '8px',
                  backgroundColor: isAuthenticated ? '#f1f5f9' : '#fff',
                  color: '#0f172a'
                }}
                placeholder="John Doe"
            />
          </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{
                display: 'block',
                fontSize: '1.05rem',
                fontWeight: '600',
                color: '#1f2937',
                marginBottom: '0.5rem'
              }}>
                <Mail size={18} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={emailValue}
                onChange={handleChange}
                required={!isAuthenticated}
                disabled={isAuthenticated}
                style={{
                  width: '100%',
                  padding: '0.95rem',
                  fontSize: '1.05rem',
                  border: '2px solid #dbeafe',
                  borderRadius: '8px',
                  backgroundColor: isAuthenticated ? '#f1f5f9' : '#fff',
                  color: '#0f172a'
                }}
                placeholder="you@company.com"
            />
            </div>

            {!isAuthenticated && (
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{
                  display: 'block',
                  fontSize: '1.05rem',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginBottom: '0.5rem'
                }}>
                  <Lock size={18} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '0.95rem',
                      paddingRight: '3rem',
                      fontSize: '1.05rem',
                      border: '2px solid #dbeafe',
                      borderRadius: '8px',
                      backgroundColor: '#fff'
                    }}
                    placeholder="Create a secure password"
                    minLength="6"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '0.9rem',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#64748b'
                    }}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '1.1rem',
                fontSize: '1.1rem',
                fontWeight: '600',
                color: '#fff',
                backgroundColor: loading ? '#7fb3ff' : '#4A90E2',
                border: 'none',
                borderRadius: '10px',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              {loading ? 'Starting checkout...' : 'Start for $19.99/mo'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SubscribePage;
